/* eslint-disable @typescript-eslint/no-explicit-any */
import { Facebook, Globe, Image as ImageIcon, Instagram, Phone, Save, Twitter, Upload } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useAdminContext, type Settings } from '../../contexts/AdminContext';
import apiClient from '../../api/client';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

export type SettingsErrors = {
  headerLogo?: string;
  footerLogo?: string;
  name?: string;
  email?: string;
  address?: string;
  phones?: {
    0?: string;
    1?: string;
  };
  socialLinks?: Record<string, string>;
  radioLink?: string;
  description?: string;
};

export default function SettingsManager() {
  const { getAdminSettings } = useAdminContext()
  const { fetchSettingsData } = useAuth()
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState<Settings>({
    socialLinks: {},
    phones: ['', ''],
  })

  const [errors, setErrors] = useState<SettingsErrors>({});

  const validateSettings = (settings: Settings) => {
    const errors: SettingsErrors = {};


    if (!settings.headerLogo) {
      errors.headerLogo = 'Header logo required';
    }
    else if (typeof settings.headerLogo === 'string') {
      // string present → skip validation
    }
    else if (!settings?.headerLogo?.type.startsWith('image/')) {
      errors.headerLogo = 'Header logo must be an image';
    } else if (settings.headerLogo.size > 5 * 1024 * 1024) {
      errors.headerLogo = 'Header logo must be under 5MB';
    }

    if (!settings.footerLogo) {
      errors.footerLogo = 'Footer logo required';
    }
    else if (typeof settings.footerLogo === 'string') {
      // string present → skip validation
    }
    else if (!settings?.footerLogo?.type.startsWith('image/')) {
      errors.footerLogo = 'Footer logo must be an image';
    } else if (settings.footerLogo.size > 5 * 1024 * 1024) {
      errors.footerLogo = 'Footer logo must be under 5MB';
    }

    if (!settings.name?.trim()) { errors.name = 'Name is required'; }
    if (!settings.radioLink?.trim()) { errors.radioLink = 'Radio link is required'; }

    if (!settings.email?.trim()) { errors.email = 'Email is required'; }
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(settings.email)) { errors.email = 'Invalid email format'; }
    
    const phones = settings.phones ?? [];

    const normalizePhone = (value?: string) =>
      value?.replace(/\s+/g, '') ?? '';

    // + + CC (1–3) + number (7–10)
    const PHONE_REGEX = /^\+\d{1,3}\d{7,10}$/;

    const phone1 = normalizePhone(phones[0]);
    const phone2 = normalizePhone(phones[1]);

    if (!phone1) {
      errors.phones = { 0: 'Primary phone number is required' };
    } else if (!PHONE_REGEX.test(phone1)) {
      errors.phones = { 0: 'Invalid phone number' };
    }

    if (phone2 && !PHONE_REGEX.test(phone2)) {
      errors.phones = {
        ...(errors.phones ?? {}),
        1: 'Invalid phone number',
      };
    }

    if (settings.socialLinks) {
      const socialErrors: Record<string, string> = {};

      const validators: Record<string, RegExp> = {
        facebook: /^(https?:\/\/)?(www\.)?(facebook\.com|fb\.com)\/.+/i,
        instagram: /^(https?:\/\/)?(www\.)?(instagram\.com|instagr\.am)\/.+/i,
        twitter: /^(https?:\/\/)?(www\.)?(twitter\.com|x\.com)\/.+/i,
        linkedin: /^(https?:\/\/)?(www\.)?linkedin\.com\/(in|company|school|pub|organization-guest)\/.+/i,
        youtube: /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\/.+/i,
      };

      for (const [platform, url] of Object.entries(settings.socialLinks)) {
        if (!url?.trim()) continue;

        const regex = validators[platform];
        if (regex && !regex.test(url.trim())) {
          socialErrors[platform] = `Invalid ${platform} URL`;
        }
      }

      if (Object.keys(socialErrors).length) {
        errors.socialLinks = socialErrors;
      }
    }

    if (!settings.description) { errors.description = 'Site description required'; }
    else if (settings.description.length > 500) { errors.description = 'Maximum 500 characters allowed'; }

    if (!settings.address) { errors.address = 'Site address required'; }
    else if (settings.address.length > 500) { errors.address = 'Maximum 500 characters allowed'; }

    return {
      isValid: Object.keys(errors).length === 0,
      errors,
    };
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const { isValid, errors } = validateSettings(settings);

    if (!isValid) {
      setLoading(false);
      setErrors(errors);
      return;
    }

    const formData = new FormData();

    if (settings.headerLogo instanceof File) {
      formData.append('headerLogo', settings.headerLogo);
    }
    if (settings.footerLogo instanceof File) {
      formData.append('footerLogo', settings.footerLogo);
    }

    formData.append('name', settings.name ?? '');
    formData.append('email', settings.email ?? '');
    formData.append('description', settings.description ?? '');
    formData.append('address', settings.address ?? '');
    formData.append('radioLink', settings.radioLink ?? '');

    formData.append('socialLinks', JSON.stringify(settings.socialLinks ?? {}));
    formData.append('phones', JSON.stringify(settings.phones ?? []));

    try {
      const { data } = await apiClient.patch(
        '/admin/update-settings',
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      )

      if (data.success) {
        toast.success(data?.message || "Settings updated successfully");
        setErrors({})
        fetchSettingsData()
      }
    }
    catch (error: any) {
      toast.error(error?.response?.data?.message || error?.message)
    }
    finally {
      setLoading(false)
    }

  };

  const handleChange = (key: keyof Settings, value: any) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleImageChange = (key: 'headerLogo' | 'footerLogo', file: File) => {
    // const reader = new FileReader();
    // reader.onload = e => handleChange(key, e.target?.result as string);
    // reader.readAsDataURL(file);
    handleChange(key, file)
  };

  const updatePhone = (index: number, value: string) => {
    setSettings(prev => ({
      ...prev,
      phones: prev?.phones?.map((p, i) => (i === index ? value : p)),
    }));
  };

  useEffect(() => {
    getAdminSettings(setSettings)
  }, [])

  const headerLogoPreview = settings.headerLogo
    ? typeof settings.headerLogo === 'string'
      ? settings.headerLogo // backend URL or base64
      : URL.createObjectURL(settings.headerLogo) // local file
    : null;

  const footerLogoPreview = settings.footerLogo
    ? typeof settings.footerLogo === 'string'
      ? settings.footerLogo // backend URL or base64
      : URL.createObjectURL(settings.footerLogo) // local file
    : null;

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-white tracking-tight">Global Settings</h1>
        <p className="text-slate-400 mt-1">Manage site configuration and contact information.</p>
      </div>

      <form onSubmit={handleSave} className="space-y-8">
        {/* Branding & Logo */}
        <section className="bg-[#0B1623] border border-white/10 rounded-2xl p-8 shadow-xl">
          <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
            <ImageIcon className="w-5 h-5 text-purple-400" />
            Branding & Logos
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-6">
            {/* Header Logo */}
            {/* <div>
              <label className="block text-sm font-medium text-slate-400 mb-2">Header Logo</label>
              <div className="bg-black/20 border border-dashed border-white/20 rounded-xl p-6 text-center hover:bg-black/40 transition-colors cursor-pointer relative group h-40 flex flex-col items-center justify-center">
                <input type="file" accept="image/*" className="absolute inset-0 opacity-0 cursor-pointer z-10" />
                <div className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                  <Upload className="w-5 h-5 text-cyan-400" />
                </div>
                <p className="text-sm text-white font-medium">Upload Header Logo</p>
                <p className="text-xs text-slate-500 mt-1">PNG, SVG (Max 2MB)</p>
              </div>
            </div> */}
            <div>
              <label className="block text-sm font-medium text-slate-400 mb-2">Header Logo</label>
              <div className="bg-black/20 border border-dashed border-white/20 rounded-xl p-6 text-center hover:bg-black/40 transition-colors cursor-pointer relative group h-40 flex flex-col items-center justify-center">
                <input
                  type="file"
                  accept="image/*"
                  className="absolute inset-0 opacity-0 cursor-pointer z-10"
                  onChange={e => e.target.files && handleImageChange('headerLogo', e.target.files[0])}
                />
                <div className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                  <Upload className="w-5 h-5 text-cyan-400" />
                </div>
                <p className="text-sm text-white font-medium">Upload Header Logo</p>
                <p className="text-xs text-slate-500 mt-1">PNG, SVG (Max 5MB)</p>
                {headerLogoPreview && <img src={headerLogoPreview} alt="Header" className="mt-2 mx-auto h-16 object-contain" />}
              </div>
              {errors.headerLogo && (
                <p className="text-red-500 text-sm mt-1">{errors.headerLogo}</p>
              )}
            </div>

            {/* Footer Logo */}
            {/* <div>
              <label className="block text-sm font-medium text-slate-400 mb-2">Footer Logo</label>
              <div className="bg-black/20 border border-dashed border-white/20 rounded-xl p-6 text-center hover:bg-black/40 transition-colors cursor-pointer relative group h-40 flex flex-col items-center justify-center">
                <input type="file" accept="image/*" className="absolute inset-0 opacity-0 cursor-pointer z-10" />
                <div className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                  <Upload className="w-5 h-5 text-cyan-400" />
                </div>
                <p className="text-sm text-white font-medium">Upload Footer Logo</p>
                <p className="text-xs text-slate-500 mt-1">PNG, SVG (Max 2MB)</p>
              </div>
            </div> */}
            <div>
              <label className="block text-sm font-medium text-slate-400 mb-2">Footer Logo</label>
              <div className="bg-black/20 border border-dashed border-white/20 rounded-xl p-6 text-center hover:bg-black/40 transition-colors cursor-pointer relative group h-40 flex flex-col items-center justify-center">
                <input
                  type="file"
                  accept="image/*"
                  className="absolute inset-0 opacity-0 cursor-pointer z-10"
                  onChange={e => e.target.files && handleImageChange('footerLogo', e.target.files[0])}
                />
                <div className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                  <Upload className="w-5 h-5 text-cyan-400" />
                </div>
                <p className="text-sm text-white font-medium">Upload Footer Logo</p>
                <p className="text-xs text-slate-500 mt-1">PNG, SVG (Max 5MB)</p>
                {footerLogoPreview && <img src={footerLogoPreview} alt="Footer" className="mt-2 mx-auto h-16 object-contain" />}
              </div>
              {errors.footerLogo && (
                <p className="text-red-500 text-sm mt-1">{errors.footerLogo}</p>
              )}
            </div>
          </div>
        </section>

        {/* General Info */}
        <section className="bg-[#0B1623] border border-white/10 rounded-2xl p-8 shadow-xl">
          <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
            <Globe className="w-5 h-5 text-cyan-400" />
            General Information
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-slate-400 mb-2">Website Name</label>
              <input
                type="text"
                placeholder={`Akaram Radio`}
                value={settings.name || ''}
                onChange={e => handleChange('name', e.target.value)}
                className="w-full px-4 py-3 bg-black/20 border border-white/10 rounded-xl focus:border-cyan-500 text-white outline-none transition-colors"
              />
              {errors.name && (
                <p className="text-red-500 text-sm mt-1">{errors.name}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-400 mb-2">Support Email</label>
              <input
                type="email"
                placeholder={`Your support email`}
                value={settings.email || ''}
                onChange={e => handleChange('email', e.target.value)}
                className="w-full px-4 py-3 bg-black/20 border border-white/10 rounded-xl focus:border-cyan-500 text-white outline-none transition-colors"
              />
              {errors.email && (
                <p className="text-red-500 text-sm mt-1">{errors.email}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-400 mb-2">Radio Link</label>
              <input
                type="text"
                placeholder={`Enter radio link`}
                value={settings.radioLink || ''}
                onChange={e => handleChange('radioLink', e.target.value)}
                className="w-full px-4 py-3 bg-black/20 border border-white/10 rounded-xl focus:border-cyan-500 text-white outline-none transition-colors"
              />
              {errors.radioLink && (
                <p className="text-red-500 text-sm mt-1">{errors.radioLink}</p>
              )}
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-slate-400 mb-2">Footer Description</label>
              <textarea
                rows={3}
                value={settings.description || ''}
                placeholder={`Enter site description`}
                onChange={e => handleChange('description', e.target.value)}
                className="w-full px-4 py-3 bg-black/20 border border-white/10 rounded-xl focus:border-cyan-500 text-white outline-none transition-colors resize-none"
              />
              {errors.description && (
                <p className="text-red-500 text-sm mt-1">{errors.description}</p>
              )}
            </div>
          </div>
        </section>

        {/* Social Media */}
        <section className="bg-[#0B1623] border border-white/10 rounded-2xl p-8 shadow-xl">
          <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
            <Facebook className="w-5 h-5 text-blue-400" />
            Social Media Links
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <div className="relative">
                <Facebook className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <input
                  type="text"
                  placeholder="Facebook URL"
                  value={settings?.socialLinks?.facebook ?? ""}
                  className="w-full pl-10 pr-4 py-3 bg-black/20 border border-white/10 rounded-xl focus:border-cyan-500 text-white outline-none transition-colors"
                  onChange={(e) => {
                    setSettings(prev => ({
                      ...prev,
                      socialLinks: { ...prev.socialLinks, facebook: e.target.value },
                    }));
                  }}
                />
              </div>
              {errors?.socialLinks?.facebook && (
                <p className="text-red-500 text-sm mt-1">{errors?.socialLinks?.facebook}</p>
              )}
            </div>

            <div>
              <div className="relative">
                <Instagram className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <input
                  type="text"
                  placeholder="Instagram URL"
                  value={settings?.socialLinks?.instagram ?? ""}
                  className="w-full pl-10 pr-4 py-3 bg-black/20 border border-white/10 rounded-xl focus:border-cyan-500 text-white outline-none transition-colors"
                  onChange={(e) => {
                    setSettings(prev => ({
                      ...prev,
                      socialLinks: { ...prev.socialLinks, instagram: e.target.value },
                    }));
                  }}
                />
              </div>
              {errors?.socialLinks?.instagram && (
                <p className="text-red-500 text-sm mt-1">{errors?.socialLinks?.instagram}</p>
              )}
            </div>

            <div>
              <div className="relative">
                <Twitter className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <input
                  type="text"
                  placeholder="Twitter URL"
                  value={settings?.socialLinks?.twitter ?? ""}
                  className="w-full pl-10 pr-4 py-3 bg-black/20 border border-white/10 rounded-xl focus:border-cyan-500 text-white outline-none transition-colors"
                  onChange={(e) => {
                    setSettings(prev => ({
                      ...prev,
                      socialLinks: { ...prev.socialLinks, twitter: e.target.value },
                    }));
                  }}
                />
              </div>
              {errors?.socialLinks?.twitter && (
                <p className="text-red-500 text-sm mt-1">{errors?.socialLinks?.twitter}</p>
              )}
            </div>

          </div>
        </section>

        {/* Contact Details */}
        <section className="bg-[#0B1623] border border-white/10 rounded-2xl p-8 shadow-xl">
          <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
            <Phone className="w-5 h-5 text-green-400" />
            Contact Details
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-slate-400 mb-2">Phone Number 1</label>
              <input
                type="tel"
                placeholder='Enter phone 1'
                className="w-full px-4 py-3 bg-black/20 border border-white/10 rounded-xl focus:border-cyan-500 text-white outline-none transition-colors"
                value={settings?.phones?.[0] ?? ""}
                onChange={(e) => {
                  updatePhone(0, e.target.value)
                }}
              />
              {errors?.phones?.[0] && (
                <p className="text-red-500 text-sm mt-1">{errors?.phones?.[0]}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-400 mb-2">Phone Number 2</label>
              <input
                type="tel"
                placeholder='Enter phone 2'
                className="w-full px-4 py-3 bg-black/20 border border-white/10 rounded-xl focus:border-cyan-500 text-white outline-none transition-colors"
                value={settings?.phones?.[1] ?? ""}
                onChange={(e) => {
                  updatePhone(1, e.target.value)
                }}
              />
              {errors?.phones?.[1] && (
                <p className="text-red-500 text-sm mt-1">{errors?.phones?.[1]}</p>
              )}
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-slate-400 mb-2">Address</label>
              <input
                type="text"
                placeholder="123 Radio St, Music City"
                value={settings.address || ''}
                onChange={e => handleChange('address', e.target.value)}
                className="w-full px-4 py-3 bg-black/20 border border-white/10 rounded-xl focus:border-cyan-500 text-white outline-none transition-colors"
              />
              {errors?.address && (
                <p className="text-red-500 text-sm mt-1">{errors.address}</p>
              )}
            </div>
          </div>
        </section>

        <div className="flex justify-end pt-4">
          <button
            type="submit"
            disabled={loading}
            className="flex items-center gap-2 bg-gradient-to-r from-cyan-600 to-blue-600 text-white px-8 py-4 rounded-xl hover:from-cyan-500 hover:to-blue-500 transition-all font-bold text-lg shadow-lg shadow-cyan-900/20 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Save className="w-5 h-5" />
            {loading ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
}
